import { lazy, Suspense } from 'react';
import type { ComponentType } from 'react';

import Loading from '/shared/components/Loading';

// Cache the entire wrapper component (not just React.lazy) per import promise.
// Without this, each call to lazyLoad returns a new arrow function that React
// treats as a different component type, causing unmount/remount of the Suspense
// tree on every route navigation - producing a visible loading flash even when
// the chunk is already downloaded.
type LazyRoute<P extends object> =
  | Promise<{ default: ComponentType<P> }>
  | (() => Promise<{ default: ComponentType<P> }>);
type LazyRouteLoader<P extends object> = () => Promise<{ default: ComponentType<P> }>;
export type PreloadableLazyRoute<P extends object> = LazyRouteLoader<P> & {
  getLoadedComponent: () => ComponentType<P> | null;
  preload: LazyRouteLoader<P>;
};
type CachedComponent = ComponentType<object>;

const wrapperCache = new Map<object, Map<boolean, CachedComponent>>();

const getCacheKey = (route: object, isWrapped: boolean) => {
  let routeMap = wrapperCache.get(route);
  if (!routeMap) {
    routeMap = new Map();
    wrapperCache.set(route, routeMap);
  }
  return routeMap.get(isWrapped);
};

const setCacheEntry = (route: object, isWrapped: boolean, component: CachedComponent) => {
  let routeMap = wrapperCache.get(route);
  if (!routeMap) {
    routeMap = new Map();
    wrapperCache.set(route, routeMap);
  }
  routeMap.set(isWrapped, component);
};

const isPreloadableLazyRoute = <P extends object>(route: LazyRoute<P>): route is PreloadableLazyRoute<P> => {
  return typeof route === 'function' && 'getLoadedComponent' in route && 'preload' in route;
};

export function createPreloadableLazyRoute<P extends object>(loader: LazyRouteLoader<P>): PreloadableLazyRoute<P> {
  let loadedComponent: ComponentType<P> | null = null;
  let loadPromise: Promise<{ default: ComponentType<P> }> | null = null;

  const load = () => {
    if (loadPromise) {
      return loadPromise;
    }

    loadPromise = loader()
      .then((loadedModule) => {
        loadedComponent = loadedModule.default;
        return loadedModule;
      })
      .catch((error: unknown) => {
        loadPromise = null;
        throw error;
      });
    return loadPromise;
  };

  return Object.assign(load, {
    getLoadedComponent: () => loadedComponent,
    preload: load,
  });
}

export default function lazyLoad<P extends object>(
  route: LazyRoute<P>,
  _isWrapped?: boolean | number,
  _isWrappedBusiness?: boolean,
) {
  const isWrapped = _isWrapped === true || !!_isWrappedBusiness;

  const cached = getCacheKey(route, isWrapped);
  if (cached) {
    return cached as ComponentType<P>;
  }

  const Component = lazy(typeof route === 'function' ? route : () => route);

  const LazyWrapper = (props: P) => {
    const LoadedComponent = isPreloadableLazyRoute(route) ? route.getLoadedComponent() : null;
    if (LoadedComponent) {
      return <LoadedComponent {...props} />;
    }

    return (
      <Suspense fallback={<Loading {...{ isWrapped }} />}>
        <Component {...props} />
      </Suspense>
    );
  };

  setCacheEntry(route, isWrapped, LazyWrapper as CachedComponent);
  return LazyWrapper;
}
